import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Divider,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  LockOpen as UnlockIcon,
  LockReset as ResetPasswordIcon,
  ForwardToInbox as ResendInviteIcon,
  PersonRemove as CancelInviteIcon,
} from '@mui/icons-material';
import { DataTable, ConfirmDialog, StatusBadge, LoadingSpinner } from '../components/shared';
import { userService, type User } from '../services';
import { getApiErrorMessage } from '../lib/apiError';
import { useAuth } from '../contexts/AuthContext';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value}</Typography>
  </Box>
);

export const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Invite Dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'USER' as 'ADMIN' | 'USER',
  });

  // Detail Dialog
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    role: 'USER' as 'ADMIN' | 'USER',
  });
  const [editUserId, setEditUserId] = useState<string | null>(null);

  // Actions Menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);

  // Confirm Dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText?: string;
    confirmColor?: 'primary' | 'error' | 'warning' | 'success';
    action: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, search, roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userService.getUsers({
        search: search || undefined,
        role: roleFilter || undefined,
        isActive: statusFilter ? statusFilter === 'active' : undefined,
        page: page + 1,
        limit: pageSize,
      });

      setUsers(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load users'));
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      setInviting(true);
      setError(null);

      await userService.inviteUser(inviteForm);

      setSuccess(`Invitation sent to ${inviteForm.email}`);
      setInviteDialogOpen(false);
      setInviteForm({
        email: '',
        firstName: '',
        lastName: '',
        role: 'USER',
      });

      await loadUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to send invitation'));
    } finally {
      setInviting(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        await userService.deactivateUser(userId);
        setSuccess('User deactivated successfully');
      } else {
        await userService.activateUser(userId);
        setSuccess('User activated successfully');
      }
      await loadUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update user status'));
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleUnlockUser = async (userId: string) => {
    try {
      await userService.unlockUser(userId);
      setSuccess('User account unlocked');
      await loadUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to unlock user'));
    }
  };

  const handleResetPassword = async (userId: string) => {
    try {
      await userService.resetUserPassword(userId);
      setSuccess('Password reset email sent');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to reset password'));
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      await userService.resendInvitation(userId);
      setSuccess('Invitation resent successfully');
    } catch (err: any) {
      // Surface the backend message (e.g. rate-limit or already-onboarded) when present.
      setError(getApiErrorMessage(err, 'Failed to resend invitation'));
    }
  };

  const handleCancelInvitation = async (userId: string) => {
    try {
      await userService.cancelInvitation(userId);
      setSuccess('Invitation cancelled successfully');
      await loadUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to cancel invitation'));
    }
  };

  const handleEditOpen = (user: User) => {
    setEditUserId(user.id);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editUserId) return;
    try {
      setEditing(true);
      setError(null);
      await userService.updateUser(editUserId, editForm);
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      await loadUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update user'));
    } finally {
      setEditing(false);
    }
  };

  const handleViewUser = async (userId: string) => {
    try {
      const user = await userService.getUser(userId);
      setSelectedUser(user);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load user details'));
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      ADMIN: 'Administrator',
      USER: 'User',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, any> = {
      ADMIN: 'error',
      USER: 'primary',
    };
    return colors[role] || 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          size="small"
          color={getRoleColor(params.value)}
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <StatusBadge status={params.value ? 'active' : 'inactive'} />
      ),
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last Login',
      width: 160,
      valueFormatter: (params) => {
        return params.value ? format(new Date(params.value), 'PPp') : 'Never';
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Users
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteDialogOpen(true)}
          size="large"
        >
          Invite User
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center' }}>
          <TextField
            label="Search"
            placeholder="Name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200, flex: { xs: '1 1 100%', md: '2 1 auto' } }}
            size="small"
          />

          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            sx={{ minWidth: 150, flex: { xs: '1 1 100%', md: '1 1 auto' } }}
            size="small"
          >
            <MenuItem value="">All Roles</MenuItem>
            <MenuItem value="ADMIN">Administrator</MenuItem>
            <MenuItem value="USER">User</MenuItem>
          </TextField>

          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150, flex: { xs: '1 1 100%', md: '1 1 auto' } }}
            size="small"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>

          {(search || roleFilter || statusFilter) && (
            <Button
              size="small"
              onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Data Table */}
      {loading && users.length === 0 ? (
        <LoadingSpinner message="Loading users..." />
      ) : (
        <DataTable
          rows={users}
          columns={columns}
          loading={loading}
          rowCount={total}
          paginationMode="server"
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSize={pageSize}
          autoHeight
        />
      )}

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => !inviting && setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
              />
            </Box>
            <TextField
              select
              label="Role"
              fullWidth
              required
              value={inviteForm.role}
              onChange={(e) =>
                setInviteForm({
                  ...inviteForm,
                  role: e.target.value as 'ADMIN' | 'USER',
                })
              }
            >
              <MenuItem value="ADMIN">Administrator</MenuItem>
              <MenuItem value="USER">User</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviting}>
            Cancel
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={
              inviting ||
              !inviteForm.email ||
              !inviteForm.firstName ||
              !inviteForm.lastName
            }
          >
            {inviting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (menuUser) handleViewUser(menuUser.id);
          handleMenuClose();
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuUser) handleEditOpen(menuUser);
          handleMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        {/* Pending users (never logged in): only invitation actions apply. */}
        {menuUser && !menuUser.lastLoginAt && (
          <MenuItem onClick={() => {
            if (menuUser) handleResendInvitation(menuUser.id);
            handleMenuClose();
          }}>
            <ListItemIcon><ResendInviteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Resend Invitation</ListItemText>
          </MenuItem>
        )}
        {menuUser && !menuUser.lastLoginAt && (
          <MenuItem onClick={() => {
            const target = menuUser;
            handleMenuClose();
            setConfirmDialog({
              open: true,
              title: 'Cancel Invitation',
              message: `Cancel the invitation for ${target.firstName} ${target.lastName} (${target.email})? This permanently removes the pending account. The same email can be invited again later.`,
              confirmText: 'Cancel Invitation',
              confirmColor: 'error',
              action: () => handleCancelInvitation(target.id),
            });
          }}>
            <ListItemIcon><CancelInviteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ color: 'error' }}
            >
              Cancel Invitation
            </ListItemText>
          </MenuItem>
        )}
        {/* Account actions only apply once the user has completed setup.
            An admin cannot deactivate their own account (server also guards
            against removing the last active administrator). */}
        {menuUser?.lastLoginAt && !(menuUser.id === currentUser?.id && menuUser.isActive) && (
          <MenuItem onClick={() => {
            if (menuUser) handleToggleStatus(menuUser.id, menuUser.isActive);
            handleMenuClose();
          }}>
            <ListItemIcon>
              {menuUser?.isActive
                ? <InactiveIcon fontSize="small" color="error" />
                : <ActiveIcon fontSize="small" color="success" />
              }
            </ListItemIcon>
            <ListItemText>{menuUser?.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
          </MenuItem>
        )}
        {menuUser?.lastLoginAt && (
          <MenuItem onClick={() => {
            if (menuUser) handleUnlockUser(menuUser.id);
            handleMenuClose();
          }}>
            <ListItemIcon><UnlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Unlock Account</ListItemText>
          </MenuItem>
        )}
        {menuUser?.lastLoginAt && (
          <MenuItem onClick={() => {
            if (menuUser) handleResetPassword(menuUser.id);
            handleMenuClose();
          }}>
            <ListItemIcon><ResetPasswordIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Reset Password</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* User Detail Dialog */}
      <Dialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedUser && (
          <>
            <DialogTitle>
              {selectedUser.firstName} {selectedUser.lastName}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <DetailRow label="Email" value={selectedUser.email} />
                <DetailRow
                  label="Role"
                  value={
                    <Chip
                      label={getRoleLabel(selectedUser.role)}
                      size="small"
                      color={getRoleColor(selectedUser.role)}
                    />
                  }
                />
                <DetailRow
                  label="Status"
                  value={
                    <StatusBadge status={selectedUser.isActive ? 'active' : 'inactive'} />
                  }
                />
                <Divider />
                <DetailRow
                  label="Two-Factor Auth"
                  value={selectedUser.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                />
                <DetailRow
                  label="Failed Login Attempts"
                  value={selectedUser.failedLoginAttempts}
                />
                {selectedUser.lockoutUntil && (
                  <DetailRow
                    label="Locked Until"
                    value={format(new Date(selectedUser.lockoutUntil), 'PPp')}
                  />
                )}
                <Divider />
                <DetailRow
                  label="Last Login"
                  value={selectedUser.lastLoginAt
                    ? format(new Date(selectedUser.lastLoginAt), 'PPp')
                    : 'Never'
                  }
                />
                <DetailRow
                  label="Last Activity"
                  value={selectedUser.lastActivityAt
                    ? format(new Date(selectedUser.lastActivityAt), 'PPp')
                    : 'Never'
                  }
                />
                <DetailRow
                  label="Created"
                  value={format(new Date(selectedUser.createdAt), 'PPp')}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelectedUser(null)} variant="contained">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editing && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Box>
            <TextField
              select
              label="Role"
              fullWidth
              required
              value={editForm.role}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  role: e.target.value as 'ADMIN' | 'USER',
                })
              }
            >
              <MenuItem value="ADMIN">Administrator</MenuItem>
              <MenuItem value="USER">User</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editing}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editing || !editForm.firstName || !editForm.lastName}
          >
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmColor={confirmDialog.confirmColor}
        onConfirm={() => {
          confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
    </Box>
  );
};
